import type { ChatMessage } from "@/zapi-frontend/stores/chatStore";

export const isVaultItemsMessage = (message: ChatMessage | undefined) => {
  try {
    return message?.role === "user" && JSON.parse(message.content)?.type === "vault_items";
  } catch {
    return false;
  }
};

// A reload in the middle of a reply leaves its empty bubble behind, so the restored chat drops it.
export const dropUnfinishedReply = (messages: ChatMessage[]): ChatMessage[] => {
  const last = messages[messages.length - 1];
  if (last?.role !== "assistant") return messages;
  try {
    const parsed = JSON.parse(last.content);
    if (parsed?.type !== "answer" || parsed.templateKey || parsed.content) return messages;
  } catch {
    return messages;
  }
  return messages.slice(0, -1);
};

// Leaving while Leo read the saved files cut the read off: the chat ends on the files message, still
// on the decision step, and the read is started again rather than sending the setup back to Save.
export const isInterruptedFilesRead = (messages: ChatMessage[], step: string) =>
  step === "decision" && isVaultItemsMessage(messages[messages.length - 1]);

export type TDecisionField = "format" | "scope" | "intent";

// The walkthrough's question for each of Section 1's fields after the decision.
export const WALKTHROUGH_TEMPLATES = {
  format: "walkthroughFormat",
  scope: "walkthroughScope",
  intent: "walkthroughIntent"
} as const satisfies Record<TDecisionField, string>;

// The walkthrough's questions a reply answers: the fields, the launch offer, the checks and the key question.
const WALKTHROUGH_QUESTION_KEYS = new Set<string>([
  ...Object.values(WALKTHROUGH_TEMPLATES),
  "walkthroughReady",
  "walkthroughCheck",
  "walkthroughQuestion"
]);

// The chat ends on a button the user pressed under one of the walkthrough's questions just before the
// page was left: `confirmReply` (the fields' "Yes") or one of the question's options. It answers a
// question the page no longer holds, so it isn't sent again as a message. Anything typed is a message
// of the user's and is left alone.
export const endsOnWalkthroughAnswer = (messages: ChatMessage[], confirmReply: string) => {
  const [asked, answer] = messages.slice(-2);
  if (answer?.role !== "user" || asked?.role !== "assistant") return false;
  try {
    const parsed = JSON.parse(asked.content);
    if (!WALKTHROUGH_QUESTION_KEYS.has(parsed?.templateKey)) return false;
    const options: unknown = parsed.templateParams?.options;
    return [confirmReply, ...(Array.isArray(options) ? options : [])].includes(answer.content.trim());
  } catch {
    return false;
  }
};

export type TOpenWalkthroughQuestion =
  | { kind: "field"; field: TDecisionField; suggestion: string }
  | { kind: "check" | "question"; text: string; options: string[] }
  | { kind: "readyOffer" };

// Leaving during the walkthrough loses it with the page: the chat ends on one of its questions that
// nothing is left to take the answer to. That question, as the walkthrough asked it.
export const openWalkthroughQuestion = (messages: ChatMessage[]): TOpenWalkthroughQuestion | null => {
  const last = messages[messages.length - 1];
  if (last?.role !== "assistant") return null;
  try {
    const parsed = JSON.parse(last.content);
    const key = parsed?.templateKey;
    const value = typeof parsed?.templateParams?.value === "string" ? parsed.templateParams.value.trim() : "";
    const options: string[] = Array.isArray(parsed?.templateParams?.options) ? parsed.templateParams.options : [];
    const field = (Object.keys(WALKTHROUGH_TEMPLATES) as TDecisionField[]).find((f) => WALKTHROUGH_TEMPLATES[f] === key);
    if (field) return value ? { kind: "field", field, suggestion: value } : null;
    if (key === "walkthroughCheck" || key === "walkthroughQuestion") {
      return value ? { kind: key === "walkthroughCheck" ? "check" : "question", text: value, options } : null;
    }
    return key === "walkthroughReady" ? { kind: "readyOffer" } : null;
  } catch {
    return null;
  }
};
